import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  ALTERNATIVE_POSITIONS,
  OTHER_QUALIFICATIONS_LEVEL,
} from "../../../../models/Onboarding/onboarding-values";
import {
  useAppSelector,
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { showNotificationAction } from "../../../../store/application/slice";
import { qualificationsThunk } from "../../../../store/onboarding/thunk";
import { TUploadedDocumentsData } from "../../../../store/onboarding/types";
import { deleteCandidateDocumentThunk } from "../../../../store/uploads/thunks";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import CheckboxInput from "../../../UI/Inputs/Checkbox";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
import { EDUCATOR_POSITIONS_BY_QUALIFICATION } from "../../Qualifications/PositionTypes";

interface EditQualificationsProps {
  handleClose: (value: boolean) => void;
  setShowRequestModal?: (value: boolean) => void;
}
const alternativePositions = [
  ALTERNATIVE_POSITIONS.ADMIN_ASSISTANT,
  ALTERNATIVE_POSITIONS.CLEANER,
  ALTERNATIVE_POSITIONS.GARDENER,
  ALTERNATIVE_POSITIONS.CHEF_COOK
];

const EditQualifications: React.FC<EditQualificationsProps> = ({
  handleClose,
  setShowRequestModal
}) => {
  const dispatch = useDispatch();
  const inProgress = useSelector((state) => state.onboarding.inProgress);
  const education = sessionStorage.getItem("qualification");
  const qualifications = useSelector(
    (state) => state.onboarding.qualifications
  );

  const userpersonaldetails = useAppSelector(
      (state) => state.onboarding.userpersonaldetails
  );
  const isPendingRequest = userpersonaldetails?.is_pending_request;
  const safeParse = (item: string) => {
    try {
      return item ? JSON.parse(item) : [];
    } catch {
      return [];
    }
  };
  const alt_positions_arr: any = sessionStorage.getItem(
    "alternative_positions"
  );
  const alt_positions = safeParse(alt_positions_arr);
  const qualification_levels_arr: any = sessionStorage.getItem(
    "qualification_accept_options"
  );
  const certificate_diploma_jobs = sessionStorage.getItem(
    "diploma_jobs_at_certificate_level"
  );
  const qualification_levels = safeParse(qualification_levels_arr);
  const [selectedAlternativePosition, setSelectedAlternativePosition] =
    useState<string[]>(
      qualifications !== null
        ? safeParse(qualifications.alternative_positions) || []
        : alt_positions || []
    );
  const [selectedQualificationLevels, setSelectedQualificationLevels] =
    useState<string[]>(
      qualifications !== null
        ? safeParse(qualifications?.qualification_accept_options) || []
        : qualification_levels || []
    );

  const [selectedEducatorPositions, setSelectedEducatorPositions] =
    useState<string[]>(() => {
      const val = qualifications?.educator_teacher_positions;
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try { return JSON.parse(val); } catch { return []; }
    });

  // Track initial chef/cook state - if checked on load, allow unchecking
  const [initialChefCookChecked] = useState<boolean>(() => {
    const initialPositions = qualifications !== null
      ? safeParse(qualifications.alternative_positions) || []
      : alt_positions || [];
    return initialPositions.includes(ALTERNATIVE_POSITIONS.CHEF_COOK);
  });

  const foodHandlerCertificate = qualifications?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "food_handler_certificate"
  );

  const cookingQualification = qualifications?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "cooking_qualification"
  );

  const educatorPositions = useMemo(() => {
      if (qualifications?.qualification === "currently_studying") {
        return EDUCATOR_POSITIONS_BY_QUALIFICATION[qualifications?.currently_studying_option || ""] ?? [];
      }
      return EDUCATOR_POSITIONS_BY_QUALIFICATION[qualifications?.qualification || ""] ?? [];
  }, [qualifications?.qualification, qualifications?.currently_studying_option]);

  const allEducatorSelected =
    educatorPositions.length > 0 &&
    educatorPositions.every((pos) => selectedEducatorPositions.includes(pos));

  const selectableAlternativePositions = alternativePositions.filter(
    (pos) => !(pos === ALTERNATIVE_POSITIONS.CHEF_COOK && !initialChefCookChecked)
  );

  const allAlternativeSelected = selectableAlternativePositions.every((pos) =>
    selectedAlternativePosition.includes(pos)
  );

  const hasSelection =
    selectedEducatorPositions.length > 0 || selectedAlternativePosition.length > 0;

  const { handleSubmit, values, errors, setFieldValue } =
    useFormik({
      validationSchema: createValidationSchema("edit_completed_qualifications"),
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        qualification: qualifications?.qualification
          ? qualifications?.qualification
          : education
          ? education
          : "",
        qualification_accept_options: selectedQualificationLevels
          ? selectedQualificationLevels
          : [],
        diploma_jobs_at_certificate_level:
          qualifications?.diploma_jobs_at_certificate_level
            ? qualifications?.diploma_jobs_at_certificate_level
            : certificate_diploma_jobs
            ? certificate_diploma_jobs
            : "",
        alternative_positions: selectedAlternativePosition
          ? selectedAlternativePosition.filter((item) => !["1", "2", "5"].includes(item))
          : [],
        educator_teacher_positions: selectedEducatorPositions,
      },
      onSubmit: async () => {
        const payload = { ...values };
        try {
          const result = await dispatch(qualificationsThunk(payload));
          if (isFulfilled(result)) {
            dispatch(showNotificationAction("Saved"))
            handleClose(false);
          }
        } catch (error) {
          throw new Error(
            "Could not connect to services at the moment. Please try again. Thanks."
          );
        }
      },
    });

  /** Filtering the value based on the degree chosen in the qualification for pg/bachelor */
  const filteredQualificationLevels = useMemo(() => {
    const otherQualificationLevels = [
      OTHER_QUALIFICATIONS_LEVEL.BACHELOR_OR_DEGREE,
      OTHER_QUALIFICATIONS_LEVEL.DIPLOMA,
      OTHER_QUALIFICATIONS_LEVEL.CERTIFICATE_III,
    ];

    const qualificationLevelsListVals =
      values.qualification === "bachelor_or_degree"
        ? otherQualificationLevels.filter(
            (qualificationLevel: string) =>
              qualificationLevel !== "Bachelor or Degree"
          )
        : otherQualificationLevels;
    return qualificationLevelsListVals;
  }, [values.qualification]);

  const handleDeleteFHCertificate = async () => {
    const candidate_uuid = sessionStorage.getItem("candidate_uuid") || "";
    await dispatch(
      deleteCandidateDocumentThunk({
        document_type: "food_handler_certificate",
        candidate_uuid: candidate_uuid,
      })
    );
  };

  const handleDeleteCookingQualification = async () => {
    const candidate_uuid = sessionStorage.getItem("candidate_uuid") || "";
    await dispatch(
      deleteCandidateDocumentThunk({
        document_type: "cooking_qualification",
        candidate_uuid: candidate_uuid,
      })
    );
  };

  const handleEducatorSelectAll = () => {
    if (allEducatorSelected) {
      setSelectedEducatorPositions(
        selectedEducatorPositions.filter((pos) => !educatorPositions.includes(pos))
      );
    } else {
      setSelectedEducatorPositions(
        Array.from(new Set([...selectedEducatorPositions, ...educatorPositions]))
      );
    }
  };

  const handleAlternativeSelectAll = () => {
    if (allAlternativeSelected) {
      setSelectedAlternativePosition([]);
    } else {
      setSelectedAlternativePosition([...selectableAlternativePositions]);
    }
  };

  const handleEducatorPositionChange = (isChecked: boolean, position: string) => {
    setSelectedEducatorPositions(
      isChecked
        ? [...selectedEducatorPositions, position]
        : selectedEducatorPositions.filter((p) => p !== position)
    );
  };

  const handleAlternativePositionChange = (isChecked: boolean, position: string) => {
    const updated = isChecked
      ? [...selectedAlternativePosition, position]
      : selectedAlternativePosition.filter((p) => p !== position);
    setSelectedAlternativePosition(updated);

    if (!isChecked && position === ALTERNATIVE_POSITIONS.CHEF_COOK) {
      if (foodHandlerCertificate?.file_path) handleDeleteFHCertificate();
      if (cookingQualification?.file_path) handleDeleteCookingQualification();
    }
  };

  /** Set the value for diploma certificate level */
  const handleDiplomaCertificateLevel = (value: string) => {
    setFieldValue("diploma_jobs_at_certificate_level", value);
  };

  /** Check and set the values in array for alternative_positions, educator_teacher_positions and qualification levels */
  useEffect(() => {
    if (selectedQualificationLevels) {
      setFieldValue("qualification_accept_options", selectedQualificationLevels);
    }
    if (selectedAlternativePosition) {
      setFieldValue("alternative_positions", selectedAlternativePosition.filter((item) => !["1", "2", "5"].includes(item)));
    }
    setFieldValue("educator_teacher_positions", selectedEducatorPositions);
  }, [setFieldValue, selectedQualificationLevels, selectedAlternativePosition, selectedEducatorPositions]);

  return (
    <div className="d-flex flex-column gap-15 fw-400 text-black">
      <div className="fs-22">Edit Qualifications</div>
      {/* Form fields */}

      <div className="d-flex flex-column gap-20">
        <div className="fs-22 fw-400 text-black">Position Types</div>
        <div className="d-flex flex-column gap-20">
          <div className="fs-16 fw-400 text-black">
            Select options below that you would like to apply for:
          </div>

          {/* Educator / Teacher Positions — only shown when qualification unlocks them */}
          {educatorPositions.length > 0 && (
            <div className="d-flex flex-column gap-10 educator_positions_container">
              <div className="d-flex align-items-center gap-5">
                <span className="fs-16 fw-700 text-black">
                  Educator/Teacher Positions:
                </span>
                <button
                  type="button"
                  className="btn btn-link p-0 fs-13 fw-700 text-decoration-underline text-black"
                  onClick={handleEducatorSelectAll}
                >
                  {allEducatorSelected ? "Deselect All" : "Select All"}
                </button>
              </div>
              {educatorPositions.map((position) => (
                <div key={position} className="theme-check">
                  <CheckboxInput
                    checked={selectedEducatorPositions.includes(position)}
                    label={position}
                    onChange={(checked) => handleEducatorPositionChange(checked, position)}
                    themeCheck={true}
                    id={uuidv4()}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Alternative Positions */}
          <div className="d-flex flex-column gap-10 alternative_positions_container mt-10px">
            <div className="d-flex align-items-center gap-5">
              <span className="fs-16 fw-700 text-black">
                Alternative Positions:
              </span>
              <button
                type="button"
                className="btn btn-link p-0 fs-14 text-decoration-underline text-black fw-700"
                onClick={handleAlternativeSelectAll}
              >
                {allAlternativeSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
            {alternativePositions.map((alternativePosition: string) => (
              <div key={alternativePosition} className="theme-check edit_alternative_position_checkbox">
                <CheckboxInput
                  checked={
                    selectedAlternativePosition &&
                    selectedAlternativePosition.includes(alternativePosition)
                  }
                  label={alternativePosition}
                  helpText={
                    alternativePosition === ALTERNATIVE_POSITIONS.CHEF_COOK ? (
                      <>
                        To apply for this position please{" "}
                        <span
                          className={`cursor-pointer ${isPendingRequest ? "" : "text-decoration-underline"}`}
                          onClick={
                            isPendingRequest
                              ? undefined
                              : () => {
                                  setShowRequestModal && setShowRequestModal(true);
                                  handleClose && handleClose(false);
                                }
                          }
                        >
                          make a request
                        </span>{" "}
                        here and upload both files.
                      </>
                    ) : undefined
                  }
                  onChange={(checked) => handleAlternativePositionChange(checked, alternativePosition)}
                  themeCheck={true}
                  id={alternativePosition}
                  disabled={alternativePosition === ALTERNATIVE_POSITIONS.CHEF_COOK && !initialChefCookChecked}
                  labelClass={alternativePosition === ALTERNATIVE_POSITIONS.CHEF_COOK && !initialChefCookChecked ? "text-grey-medium" : ""}
                />
              </div>
            ))}
            {errors && (
              <ErrorMessage message={errors.alternative_positions as string} />
            )}
          </div>
        </div>
      </div>
      <div className="w-100 h-1px"></div>
      <div className="d-flex justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          onClick={handleSubmit}
          isLoading={inProgress}
          disabled={!hasSelection}
          contentClass="d-flex"
        >
          <RoundedTick color={inProgress || !hasSelection ? "#aaaaaa" : "black"} />
          Save
        </CustomButton>
      </div>
    </div>
  );
};

export default EditQualifications;
